import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import background from "./images/background.png";
import { X } from "lucide-react";

const API = process.env.REACT_APP_API_URL || "";

export default function Profile() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("perfil");
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ total: 0, rank: "-" });
  const [loading, setLoading] = useState(true);
  const [caguamas, setCaguamas] = useState({
    easy: 0,
    medium: 0,
    hard: 0,
    total: 0,
  });
  const [showCaguamaInfo, setShowCaguamaInfo] = useState(false);

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!user) navigate("/home");
  }, [user]);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchProfileAndCheckMigration();
    fetchStats();
    fetchCaguamas();
  }, [token]);

  const fetchProfileAndCheckMigration = async () => {
    try {
      const res = await fetch(`${API}/api/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) return;
      setProfile(data);
      setEmailInput(data.email || "");
      const quotesPoints = parseInt(
        localStorage.getItem("karime_game_high_score_v1") || "0"
      );
      const sopasPoints = parseInt(
        localStorage.getItem("kk_sopas_completadas_ever") || "0"
      );
      if ((quotesPoints > 0 || sopasPoints > 0) && !data.points_migrated) {
        navigate("/migrar", {
          state: { token, quotes: quotesPoints, sopas: sopasPoints },
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/scores/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) setStats({ total: data.total || 0, rank: data.rank || "-" });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCaguamas = async () => {
    try {
      const res = await fetch(`${API}/api/games/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setCaguamas(
          data.caguamas
            ? { ...data.caguamas, total: data.total }
            : { easy: 0, medium: 0, hard: 0, total: 0 }
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/home");
  };

  const handlePasswordChange = async () => {
    setPasswordError("");
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPasswordError("Llena los tres campos bb");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError("Mínimo 6 caracteres");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordError("Las contraseñas no coinciden");
      return;
    }
    try {
      const res = await fetch(`${API}/api/auth/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPasswordError(data.error);
        return;
      }
      setPasswordSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      setTimeout(() => {
        setPasswordSuccess(false);
        setShowPasswordForm(false);
      }, 2000);
    } catch {
      setPasswordError("Error de conexión");
    }
  };

  const handleEmailUpdate = async () => {
    setEmailError("");
    if (emailInput.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailInput.trim())) {
        setEmailError("Formato de correo inválido");
        return;
      }
    }
    try {
      const res = await fetch(`${API}/api/auth/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: emailInput }),
      });
      const data = await res.json();
      if (!res.ok) {
        setEmailError(data.error);
        return;
      }
      setEmailSuccess(true);
      fetchProfileAndCheckMigration();
      setTimeout(() => {
        setEmailSuccess(false);
        setShowEmailForm(false);
      }, 2000);
    } catch {
      setEmailError("Error de conexión");
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError("");
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/account`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json();
        setDeleteError(data.error || "Error al eliminar cuenta");
        return;
      }
      logout();
      navigate("/home");
    } catch {
      setDeleteError("Error de conexión");
    } finally {
      setDeleteLoading(false);
    }
  };

  const inputStyle = {
    background: "white",
    border: "1.5px solid #e0d0f0",
    borderRadius: "12px",
    padding: "10px 12px",
    fontSize: "13px",
    fontFamily: "Quicksand, sans-serif",
    outline: "none",
    width: "100%",
    color: "#2d1f3d",
  };

  if (!user) return null;
  if (loading)
    return (
      <div
        style={{
          minHeight: "calc(100vh - 120px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Quicksand, sans-serif",
        }}
      >
        <p style={{ color: "#b06ab3" }}>Cargando...</p>
      </div>
    );

  return (
    <div
      className="flex flex-col font-quicksand"
      style={{
        backgroundImage: `url(${background})`,
        backgroundRepeat: "repeat-y",
        backgroundSize: "100% auto",
        backgroundPosition: "top center",
        minHeight: "calc(100vh - 120px)",
      }}
    >
      <div className="px-5 pt-6 pb-10 flex flex-col gap-5">
        {/* Avatar + username */}
        <div style={{ textAlign: "center", paddingTop: "8px" }}>
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #b06ab3, #c4b5fd)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "24px",
              fontWeight: 800,
              color: "white",
              margin: "0 auto 12px",
              boxShadow: "0 4px 16px rgba(176,106,179,0.3)",
            }}
          >
            {user.username[0].toUpperCase()}
          </div>
          <p style={{ fontSize: "18px", fontWeight: 800, color: "#2d1f3d" }}>
            @{user.username}
          </p>
          <p style={{ fontSize: "12px", color: "#9b7eaa", marginTop: "4px" }}>
            Miembro desde{" "}
            {new Date(profile?.created_at).toLocaleDateString("es-MX", {
              month: "long",
              year: "numeric",
            })}{" "}
            🩵
          </p>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            background: "rgba(255,255,255,0.6)",
            borderRadius: "999px",
            padding: "4px",
            border: "1.5px solid #e8e0f0",
          }}
        >
          {[
            { key: "perfil", label: "Perfil" },
            { key: "logros", label: "Mis logros ✨" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                flex: 1,
                padding: "9px",
                borderRadius: "999px",
                border: "none",
                fontSize: "13px",
                fontWeight: 700,
                fontFamily: "Quicksand, sans-serif",
                cursor: "pointer",
                transition: "all 0.2s",
                background: activeTab === key ? "white" : "transparent",
                color: activeTab === key ? "#b06ab3" : "#9b7eaa",
                boxShadow:
                  activeTab === key
                    ? "0 2px 8px rgba(176,106,179,0.15)"
                    : "none",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── TAB: Mis logros ── */}
        {activeTab === "logros" && (
          <>
            {/* Stats globales */}
            <div style={{ display: "flex", gap: "10px" }}>
              {[
                { num: stats.total, label: "pts globales" },
                {
                  num: stats.rank === 1 ? "👑" : `#${stats.rank}`,
                  label: "ranking",
                },
              ].map((s, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    background: "rgba(255,255,255,0.92)",
                    borderRadius: "18px",
                    border: "1.5px solid #e8e0f0",
                    padding: "14px",
                    textAlign: "center",
                    boxShadow: "0 2px 10px rgba(176,106,179,0.08)",
                  }}
                >
                  <p
                    style={{
                      fontSize: "22px",
                      fontWeight: 800,
                      color: "#b06ab3",
                      margin: 0,
                    }}
                  >
                    {s.num}
                  </p>
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#9b7eaa",
                      fontWeight: 600,
                      marginTop: "4px",
                    }}
                  >
                    {s.label}
                  </p>
                </div>
              ))}
            </div>

            {/* Récords personales */}
            <div style={{ display: "flex", gap: "10px" }}>
              {[
                {
                  num: profile?.record_completa_frase || 0,
                  label: "récord frases ✨",
                },
                { num: profile?.record_sopas || 0, label: "récord sopas 🧩" },
              ].map((s, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    background: "rgba(255,255,255,0.92)",
                    borderRadius: "18px",
                    border: "1.5px solid #e8e0f0",
                    padding: "14px",
                    textAlign: "center",
                    boxShadow: "0 2px 10px rgba(176,106,179,0.08)",
                  }}
                >
                  <p
                    style={{
                      fontSize: "22px",
                      fontWeight: 800,
                      color: "#7BDCFB",
                      margin: 0,
                    }}
                  >
                    {s.num}
                  </p>
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#9b7eaa",
                      fontWeight: 600,
                      marginTop: "4px",
                    }}
                  >
                    {s.label}
                  </p>
                </div>
              ))}
            </div>

            {/* Caguamas */}
            <div
              style={{
                background: "rgba(255,255,255,0.92)",
                borderRadius: "20px",
                border: "1.5px solid #e8e0f0",
                padding: "16px",
                boxShadow: "0 2px 10px rgba(176,106,179,0.08)",
              }}
            >
              {/* Título + botón (i) */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "12px",
                }}
              >
                <p
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#b06ab3",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    margin: 0,
                  }}
                >
                  Caguamas ganadas 🍺
                </p>
                <button
                  onClick={() => setShowCaguamaInfo(true)}
                  style={{
                    background: "none",
                    border: "1.5px solid #c4b5fd",
                    borderRadius: "50%",
                    width: "18px",
                    height: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    padding: 0,
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      fontSize: "10px",
                      fontWeight: 800,
                      color: "#b06ab3",
                      lineHeight: 1,
                    }}
                  >
                    i
                  </span>
                </button>
              </div>

              {/* Total */}
              <div
                style={{
                  textAlign: "center",
                  paddingBottom: "12px",
                  borderBottom: "1px solid #f5f0fa",
                  marginBottom: "12px",
                }}
              >
                <p
                  style={{
                    fontSize: "32px",
                    fontWeight: 800,
                    color: "#b06ab3",
                    margin: 0,
                  }}
                >
                  {caguamas.total || 0}
                </p>
                <p
                  style={{
                    fontSize: "11px",
                    color: "#9b7eaa",
                    fontWeight: 600,
                    marginTop: "2px",
                  }}
                >
                  en total
                </p>
              </div>

              {/* Por dificultad */}
              <div style={{ display: "flex", gap: "8px" }}>
                {[
                  { key: "easy", emoji: "🟢", label: "Cute", color: "#4ade80" },
                  {
                    key: "medium",
                    emoji: "🟣",
                    label: "Coquetona",
                    color: "#b06ab3",
                  },
                  {
                    key: "hard",
                    emoji: "🔴",
                    label: "Perrísima",
                    color: "#f87171",
                  },
                ].map(({ key, emoji, label, color }) => (
                  <div
                    key={key}
                    style={{
                      flex: 1,
                      textAlign: "center",
                      background: "#faf8ff",
                      borderRadius: "14px",
                      padding: "10px 4px",
                      border: "1.5px solid #f0e8ff",
                    }}
                  >
                    <p style={{ fontSize: "16px", margin: "0 0 2px" }}>
                      {emoji}
                    </p>
                    <p
                      style={{
                        fontSize: "20px",
                        fontWeight: 800,
                        color,
                        margin: 0,
                      }}
                    >
                      {caguamas[key] || 0}
                    </p>
                    <p
                      style={{
                        fontSize: "10px",
                        color: "#9b7eaa",
                        fontWeight: 600,
                        marginTop: "2px",
                      }}
                    >
                      {label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── TAB: Perfil ── */}
        {activeTab === "perfil" && (
          <>
            <div
              style={{
                background: "rgba(255,255,255,0.92)",
                borderRadius: "20px",
                overflow: "hidden",
                border: "1.5px solid #e8e0f0",
                boxShadow: "0 2px 10px rgba(176,106,179,0.08)",
              }}
            >
              {/* Username */}
              <div
                style={{
                  padding: "14px 16px",
                  borderBottom: "1px solid #f5f0fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#b06ab3"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                  </svg>
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#2d1f3d",
                    }}
                  >
                    Username
                  </span>
                </div>
                <span style={{ fontSize: "13px", color: "#9b7eaa" }}>
                  @{profile?.username}
                </span>
              </div>

              {/* Contraseña */}
              <div style={{ borderBottom: "1px solid #f5f0fa" }}>
                <div
                  style={{
                    padding: "14px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setShowPasswordForm(!showPasswordForm);
                    setPasswordError("");
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmNewPassword("");
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#b06ab3"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#2d1f3d",
                      }}
                    >
                      Contraseña
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#7BDCFB",
                      fontWeight: 700,
                    }}
                  >
                    {showPasswordForm ? "Cancelar" : "Cambiar"}
                  </span>
                </div>
                {showPasswordForm && (
                  <div
                    style={{
                      padding: "0 16px 16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <input
                      type="password"
                      placeholder="Contraseña actual"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      style={inputStyle}
                    />
                    <input
                      type="password"
                      placeholder="Nueva contraseña"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      style={inputStyle}
                    />
                    <input
                      type="password"
                      placeholder="Confirmar nueva contraseña"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      style={inputStyle}
                    />
                    {passwordError && (
                      <p style={{ fontSize: "11px", color: "#f87171" }}>
                        {passwordError}
                      </p>
                    )}
                    {passwordSuccess && (
                      <p style={{ fontSize: "11px", color: "#4ade80" }}>
                        ¡Contraseña actualizada! ✨
                      </p>
                    )}
                    <button
                      onClick={handlePasswordChange}
                      style={{
                        background: "#7BDCFB",
                        border: "none",
                        borderRadius: "999px",
                        padding: "10px",
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "white",
                        fontFamily: "Quicksand, sans-serif",
                        cursor: "pointer",
                      }}
                    >
                      Guardar contraseña
                    </button>
                  </div>
                )}
              </div>

              {/* Correo */}
              <div>
                <div
                  style={{
                    padding: "14px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setShowEmailForm(!showEmailForm);
                    setEmailError("");
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#b06ab3"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="M2 8l10 7 10-7" />
                    </svg>
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#2d1f3d",
                      }}
                    >
                      Correo
                    </span>
                    <span
                      style={{
                        fontSize: "10px",
                        color: "#b06ab3",
                        fontWeight: 600,
                        background: "#f0e8f8",
                        padding: "2px 8px",
                        borderRadius: "999px",
                      }}
                    >
                      opcional
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#7BDCFB",
                      fontWeight: 700,
                    }}
                  >
                    {showEmailForm
                      ? "Cancelar"
                      : profile?.email
                      ? "Editar"
                      : "Agregar"}
                  </span>
                </div>
                {!showEmailForm && profile?.email && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#9b7eaa",
                      padding: "0 16px 12px",
                    }}
                  >
                    {profile.email}
                  </p>
                )}
                {showEmailForm && (
                  <div
                    style={{
                      padding: "0 16px 16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <input
                      type="email"
                      placeholder="tu@correo.com"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      style={inputStyle}
                    />
                    {emailError && (
                      <p style={{ fontSize: "11px", color: "#f87171" }}>
                        {emailError}
                      </p>
                    )}
                    {emailSuccess && (
                      <p style={{ fontSize: "11px", color: "#4ade80" }}>
                        ¡Correo guardado! ✨
                      </p>
                    )}
                    <button
                      onClick={handleEmailUpdate}
                      style={{
                        background: "#7BDCFB",
                        border: "none",
                        borderRadius: "999px",
                        padding: "10px",
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "white",
                        fontFamily: "Quicksand, sans-serif",
                        cursor: "pointer",
                      }}
                    >
                      Guardar correo
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Cerrar sesión */}
            <button
              onClick={handleLogout}
              style={{
                background: "rgba(255,255,255,0.92)",
                border: "1.5px solid #7BDCFB",
                borderRadius: "999px",
                padding: "13px",
                fontSize: "14px",
                fontWeight: 700,
                color: "#7BDCFB",
                fontFamily: "Quicksand, sans-serif",
                cursor: "pointer",
              }}
            >
              Cerrar sesión
            </button>

            {/* Eliminar cuenta */}
            {!showDeleteConfirm ? (
              <p
                onClick={() => setShowDeleteConfirm(true)}
                style={{
                  fontSize: "12px",
                  color: "#f87171",
                  textAlign: "center",
                  cursor: "pointer",
                }}
              >
                Eliminar cuenta
              </p>
            ) : (
              <div
                style={{
                  background: "rgba(255,255,255,0.92)",
                  borderRadius: "20px",
                  border: "1.5px solid #fecaca",
                  padding: "18px 16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <p
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#2d1f3d",
                    margin: 0,
                    textAlign: "center",
                  }}
                >
                  ¿Eliminar tu cuenta?
                </p>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#9b7eaa",
                    margin: 0,
                    textAlign: "center",
                    lineHeight: 1.5,
                  }}
                >
                  Esto borrará tu cuenta y todos tus puntos para siempre. No hay
                  vuelta atrás 😢
                </p>
                {deleteError && (
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#f87171",
                      textAlign: "center",
                      margin: 0,
                    }}
                  >
                    {deleteError}
                  </p>
                )}
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteError("");
                    }}
                    style={{
                      flex: 1,
                      background: "rgba(255,255,255,0.92)",
                      border: "1.5px solid #e0d0f0",
                      borderRadius: "999px",
                      padding: "10px",
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#9b7eaa",
                      fontFamily: "Quicksand, sans-serif",
                      cursor: "pointer",
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading}
                    style={{
                      flex: 1,
                      background: deleteLoading ? "#fca5a5" : "#f87171",
                      border: "none",
                      borderRadius: "999px",
                      padding: "10px",
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "white",
                      fontFamily: "Quicksand, sans-serif",
                      cursor: deleteLoading ? "not-allowed" : "pointer",
                    }}
                  >
                    {deleteLoading ? "Eliminando..." : "Sí, eliminar"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal info caguamas */}
      {showCaguamaInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 font-quicksand">
          <div
            className="bg-white p-6 max-w-md mx-4 relative"
            style={{
              borderRadius: "20px",
              border: "2px solid #f9a8d4",
              boxShadow: "0 8px 32px rgba(176,106,179,0.15)",
            }}
          >
            {/* X button */}
            <button
              onClick={() => setShowCaguamaInfo(false)}
              className="absolute top-3 right-3 hover:scale-110 transition-transform"
              style={{ color: "#c4b5fd" }}
            >
              <X size={20} />
            </button>

            <div className="text-center mb-5">
              <h3
                className="text-lg font-bold mb-4"
                style={{ color: "#b06ab3" }}
              >
                ¿Qué es una caguama? 🍺
              </h3>
              <p
                style={{ color: "#6b7280" }}
                className="text-sm leading-relaxed"
              >
                Una caguama se gana cuando logras una racha seguida en el juego
                de{" "}
                <strong style={{ color: "#b06ab3" }}>Completa la frase</strong>.
                Cada nivel tiene su propio contador — Cute, Coquetona y
                Perrísima.
              </p>
              <p
                className="italic mt-4 text-sm font-semibold"
                style={{ color: "#b06ab3" }}
              >
                Solo disponible en ese juego por ahora 😉
              </p>
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => setShowCaguamaInfo(false)}
                className="px-5 py-2 bg-custom-blue text-white rounded-full active:scale-95 transition-transform"
                style={{ boxShadow: "0 4px 12px rgba(125,211,252,0.4)" }}
              >
                ¡Entendido!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
